import { Combobox, DatePicker, Errors, Field, FormValue, RadioboxGroup, SimpleRadiobox, TextInput, WithForm, validation } from '@startlibs/form';
import {Dialog, Button, SplitColumnsContainer, ConfirmDialog, Loading, Icon, SwitchDiv} from '@startlibs/components'
import {addQueryString, callIfFunction, formFetch, formatDate, getColor, getFetcher, media, postFetcher, smoothScroll} from '@startlibs/utils'
import React, { useEffect, useState } from 'react'
import _ from 'lodash/fp'

import {useNotificationRef} from '../../components/Notifications'
import styled, { css } from 'styled-components';
import { Fill, Slot, useConstant, useRefState, useToggle } from '@startlibs/core';
import { CurrentPatientStudyCard } from './CurrentPatientStudyCard';
import { callUpdateParamsPat, useRegisterSetParamsPat, useUpdateParamsPat } from '../../hooks/useUpdateParamsPat';
import { cachedPatientFetcher } from '../utils/cachedPatientFetcher';
import { Pagination } from '../../components/Pagination';
import { FetchErrorDialog } from '../errors/FetchErrorDialog';
import { dicomDateToDate, formatNoUTCDate } from '../../utils';
import { CardHeader } from '../errors/ProviderError';
import { StudyCard } from './StudyCard';
import { UNARCHIVING } from '../../utils/utils';

const ErrorCard = styled.div`
  padding: 2.5rem;
  background: white;
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.15);
  max-width: 40rem;
  margin: 0 auto;
  p {
    font-size: 14px;
  }
`

const FlexDiv = styled.div`
  display: flex;
  -webkit-flex-direction: row;
  -ms-flex-direction: row;
  flex-direction: row;
  flex-wrap: nowrap;
  align-content: center;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
`

const LoadingDiv = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: center;
  align-items: center;
  flex-grow: 1;
`

const SearchInput = styled.div`
  position: relative;
  width: 100%;
  position: sticky;
  top: 0;
  z-index: 100;
  > input {
    word-break: normal;
    padding-right: 2.75rem;
    padding-left: 2.75rem;
    border-radius: 0px;
    border: none;
    border-bottom: 1px solid rgba(0,0,0,0.18);
    :focus {
      box-shadow: none;
      border-bottom: 1px solid rgba(0,0,0,0.18);
    }
  }
`

const ClearSearchButton = styled(Icon)`
  color: ${getColor('gray90')};
  font-size: 12px;
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  width: 1.5rem;
  line-height: 1.5rem;
  text-align: center;
  border-radius: 50%;
  background: rgba(0,0,0,0.1);
  :hover {
    background: #f7d3d4;
    color: ${getColor('alert')};
  }
`

const SearchIcon = styled(Icon)`
  position: absolute;
  z-index: 100;
  left: 8px;
  bottom: 7px;
  font-size: 22px;
  color: ${getColor('gray150')};
`

const EmptyList = styled.div `
  background: ${getColor('gray240')};
  color: ${getColor('gray120')};
  height: 100%;
  padding: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  font-size: 14px;
  flex-grow: 1;
`

const PatientList = styled.div`
  margin-top: 1.5rem;
  border: 1px solid ${getColor('gray210')};
  border-radius: 5px;
  overflow: hidden;
`

const StyledPatientRow = styled.div`
  cursor: pointer;
  &:nth-child(even) {
    background-color: #EFF4F7;
  }
  &:hover {
    background: rgba(0,139,210,0.15);
  }
  font-size: 13px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid ${getColor('gray210')};
  padding: 1rem;
  &:last-child {
    border-bottom: 0px;
  }
  .info-box {
    width: 260px;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    align-content: center;
    justify-content: space-between;
    align-items: center;
    margin-top: 0.15rem;
  }
  ${props => props.selected && css`
    &:nth-child(odd), &:nth-child(even) {
      background-color: ${getColor('main')};
      color: white;
      &:hover {
        background: ${getColor('main')};
      }
    }
    ${Icon} {
      position: absolute;
      left: 50%;
      right: 50%;
      transform: translate(-50%,-50%);
      color: ${getColor('main')};
      width: 10px;
      height: 11px;
      font-weight: 600;
    }
  `}
`

const PatientRadioBox = styled(SimpleRadiobox)`
  padding: 0;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  > div > div {
    position: static;
    > div {
      left: 50%;
      transform: translate(-50%,-50%);
      ${props => props.selected && css`
        background: white;
        border: transparent;
        &:before {
          background: transparent;
        }
      `}
    }
    > span {
      display: none;
    }
  }
`

const ListArea = styled.div`
  border: 1px solid ${getColor('gray210')};
  border-radius: 6px;
  min-height: 200px;
  max-height: 320px;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  margin-bottom: 1rem;
  .table-footer {
    padding: 0.5rem;
    display: flex;
    flex-wrap: nowrap;
    align-content: center;
    justify-content: center;
    align-items: center;
    background: white;
    width: 100%;
    box-shadow: 0 -1px 0 0 rgb(210,210,210);
    border-radius: 0px 0px 5px 5px;
    position: sticky;
    bottom: -1px;
    margin-top: auto;
  }
  ::-webkit-scrollbar {
    width: 16px;
    height: 16px;
    background: rgba(0,0,0,0.05);
    border-radius: 0 5px 5px 0;
  }
  ::-webkit-scrollbar-thumb {
    background: rgba(0,0,0,0.25);
    border-radius: 100px;
    background-clip: padding-box;
    border: 4px solid transparent;
    min-height: 30px;
    min-width: 30px;
    &:hover {
      background: rgba(0,0,0,0.35);
      background-clip: padding-box;
      border: 4px solid transparent;
    }
  }
`

const TableLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  h4 {
    margin-bottom: 0.5rem;
    font-size: 14px;
  }
`

export const buildName = (nameObject) => {
  const nameArray = [
    nameObject?.lastName,
    nameObject?.firstName,
    nameObject?.middleName,
    nameObject?.prefix,
    nameObject?.suffix,
  ]
  const nameString = nameArray.join("^")
  return nameString
}

const splitName = (name) => {
  const nameArray = name.split("^")
  const nameObject = {
    lastName: nameArray[0],
    firstName: nameArray[1],
    middleName: nameArray[2],
    prefix: nameArray[3],
    suffix: nameArray[4],
  }
  return nameObject
}


export const UnarchiveDialog = ({study, setStudy, closeDialog, unarchivingPanel, setUnarchivingWorklist, setInvalidateCache}) => {
  const [,setNotification] = useNotificationRef()
  const UNARCHIVING_STARTED_NOTIFICATION_MSG = "Unarchiving may take a few minutes. You can continue working."

  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [patientData, setPatientData] = useState({})

  // const studyUid = study.tools.dicomEditWeblink.split("study_iuid=")[1]
  const studyUid = study.study.iuid;

  useEffect(() => {
  
    // getting the stuy info direct from databse (may have been updated)
    const getDicomTagsUrl = `/getUpdateStudyInfo.php?study_iuid=${studyUid}`
    getFetcher(getDicomTagsUrl)
      .then((response) => {
        const nameObject = splitName(response.patientName)
        const newPatientData = {
          patientID: response.patientID,
          firstName: nameObject.firstName,
          middleName: nameObject.middleName,
          lastName: nameObject.lastName,
          patientName: response.patientName,
          patientSex: response.patientSex,
          patientDOB: response.patientDOB,
          institutionName: response.institutionName,
          study_desc: response.study_desc,
          countStudies: response.countStudies,
          patientPK: response.patientPK
        }
        setPatientData(newPatientData)
        setIsLoading(false)  
      })
      .catch((error) => {
        setLoadError(true)
        setIsLoading(false)
      });

  },[])

  
  return <Dialog
      title="Unarchive study"
      closeDialog={closeDialog}
      footer={ () => {
        return <>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button type="submit" highlight 
            disabled={isLoading}
            onClick={() => {
              setIsSubmitting(true)
              // getFetcher('/StudyRotateUnarchive.php?id_study=' + studyUid)
              postFetcher('/StudyRotateUnarchive.php',{
                studyDescription: patientData.study_desc || "",
                patientID: patientData.patientID || "",
                patientName: patientData.patientName || "",
                studyUID: studyUid
              })
                .then((response) => {
                  setIsSubmitting(false)
                  closeDialog()
                  setStudy((study) => {
                    return {
                      ...study,
                      study: {
                        ...study.study,
                        rotate_status: UNARCHIVING,
                      }
                    }
                  })
                  setInvalidateCache(true)
                  setUnarchivingWorklist((unarchivingWorklist) => {
                    // Add study to unarchiving worklist
                    const newUnarchivingWorklist = [...unarchivingWorklist];
                    newUnarchivingWorklist.push({
                      ...study,
                      study: {
                        ...study.study,
                        rotate_status: UNARCHIVING,
                      },
                    });
                    return newUnarchivingWorklist;
                  });
                  unarchivingPanel.open()
                  setTimeout(() => {
                    setNotification(UNARCHIVING_STARTED_NOTIFICATION_MSG)
                  }, 200)
                })
                .catch((e) => {
                  setIsSubmitting(false)
                  console.log('error', e)
                  setNotification({type:"alert",msg:<>It was not possible to unarchive this study. Please try again later.</>})
                  closeDialog()
                })
            }}
            isLoading={isSubmitting}>
            Unarchive
          </Button>
        </>}
      }
    >
      {isLoading 
        ? <LoadingDiv css="height:200px;">
            <Loading size={30} borderWidth={5}/>
          </LoadingDiv> 
        : loadError 
          ? <ErrorCard>
              <CardHeader>
                <h1>Something went wrong</h1>
              </CardHeader>
              <p><b>Please reload the page and try again.</b></p>
              <p>If the error persists please contact <a href="mailto:support@purview.net"
                                                        target="_blank">support@purview.net</a> or <span
                className="nowrap">call <b>+1 800-501-1537</b>.</span></p>
            </ErrorCard>
          : <>
              {/* <p css="margin-bottom:0.25rem;"><b>Would you like to unarchive this study?</b></p>  */}
              <p>This process may take a few minutes</p>
              {/* <CurrentPatientStudyCard study={study} patientName={patientData.patientName} patientID={patientData.patientID} patientDOB={patientData.patientDOB}/> */}
              <StudyCard study={study} showRefPhysican/>
            </>  
      }
    </Dialog>
}
